// Constants
import {
  COMPLETE_OPENBANKING_TRACKING_ERROR,
  INVALIDATE_OPENBANKING_TRACKING_ERROR,
  WS_PROOF_INCOME_TRACKING_ERROR,
} from './constants/proof-income-method.handlers.constants';
// Enumerations
import { AppStatesEnumeration } from 'enumerations/app-states.enumeration';
import { WebSocketPayloadStatusEnumeration } from 'enumerations/wbsocket-payload-status.enumeration';
// Resolvers
import {
  completeOpenbankingResolver,
  invalidateOpenbankingResolver,
} from '../resolvers/proof-income-method.resolver';
// Types
import { WsResponseType } from 'types/ws-response.type';
import {
  ProofIncomeMethodsBuilderHandlersType,
  ProofIncomeMethodsCompleteBuilderHandlersType,
  ProofIncomeMethodsIframeBuilderHandlersType,
  ProofIncomeMethodsInvalidateBuilderHandlersType,
  ProofIncomeMethodsMethodBuilderHandlersType,
  ProofIncomeMethodsMethodsHandlersType,
} from './types/proof-income-method.handlers.type';
// Utils
import { eventIsNotFromTink, isErrorType, isReportsType } from 'utils/identification.utils';

const manageReceivedDataFromWSEventHandler = ({
  handleTrackErrorView,
  message,
  setCurrentState,
  setError,
}: ProofIncomeMethodsMethodBuilderHandlersType): void => {
  const response = JSON.parse(message as any);
  switch (response?.detail?.payload?.status) {
    case WebSocketPayloadStatusEnumeration.ACCEPTED:
      setCurrentState(AppStatesEnumeration.LOADING);
      break;
    case WebSocketPayloadStatusEnumeration.REJECTED:
    default:
      setError(true);
      handleTrackErrorView(WS_PROOF_INCOME_TRACKING_ERROR);
  }
};

const completeOpenbankingHandler = async ({
  handleTrackErrorView,
  proofIncomeIdentificationId,
  reportIds,
  setCurrentState,
  setError,
}: ProofIncomeMethodsCompleteBuilderHandlersType): Promise<void> => {
  const [error, status] = await completeOpenbankingResolver({
    proofIncomeIdentificationId,
    reportIds,
  });
  if (error) {
    setError(true);
    handleTrackErrorView({
      ...COMPLETE_OPENBANKING_TRACKING_ERROR,
      errorNumber: status,
    });
  } else {
    setCurrentState(AppStatesEnumeration.LOADING);
  }
};

const invalidateOpenbankingHandler = async ({
  handleTrackErrorView,
  proofIncomeIdentificationId,
  setError,
}: ProofIncomeMethodsInvalidateBuilderHandlersType): Promise<void> => {
  const [, status] = await invalidateOpenbankingResolver(proofIncomeIdentificationId);
  setError(true);
  handleTrackErrorView({
    ...INVALIDATE_OPENBANKING_TRACKING_ERROR,
    errorNumber: status,
  });
};

const manageReceiveDataFromIframeEventHandler = async ({
  event,
  handleTrackErrorView,
  proofIncomeIdentificationId,
  setError,
  setCurrentState,
}: ProofIncomeMethodsIframeBuilderHandlersType): Promise<void> => {
  if (eventIsNotFromTink(event.origin)) {
    return;
  }
  const { type, data, error } = JSON.parse(event.data);
  if (isReportsType(type)) {
    const { account_verification_report_id, transaction_report_id } = data;
    completeOpenbankingHandler({
      handleTrackErrorView,
      proofIncomeIdentificationId,
      setCurrentState,
      setError,
      reportIds: { account_verification_report_id, transaction_report_id },
    });
  } else if (isErrorType(type) || error) {
    invalidateOpenbankingHandler({ handleTrackErrorView, proofIncomeIdentificationId, setError });
  }
};

const ProofIncomeMethodHandlers = (
  props: ProofIncomeMethodsBuilderHandlersType
): ProofIncomeMethodsMethodsHandlersType => ({
  handleManageReceivedDataFromWSEvent: (
    message: WsResponseType<WebSocketPayloadStatusEnumeration>
  ) => manageReceivedDataFromWSEventHandler({ ...props, message }),
  handleManageReceiveDataFromIframeEvent: (event: MessageEvent) =>
    manageReceiveDataFromIframeEventHandler({ ...props, event }),
});

export { ProofIncomeMethodHandlers };
